import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useOturum } from './lib/oturum.js';
import { Kabuk } from './Kabuk.js';
import { Giris } from './moduller/Giris.js';
import { AnaSayfa } from './moduller/AnaSayfa.js';
import { IsEmirleri } from './moduller/IsEmirleri.js';
import { IsEmriDetayi } from './moduller/IsEmriDetayi.js';
import { Musteriler } from './moduller/Musteriler.js';
import { MusteriDetayi } from './moduller/MusteriDetayi.js';
import { Katalog } from './moduller/Katalog.js';
import { Kasa } from './moduller/Kasa.js';
import { Proforma, ProformaDetayi } from './moduller/Proforma.js';
import { Stok } from './moduller/Stok.js';
import { Yonetici } from './moduller/Yonetici.js';
import { Ayarlar } from './moduller/Ayarlar.js';
import { Yukleniyor } from './bilesenler/temel.js';
import { Gizlilik, Kosullar, OauthTanitim, kamusalMi } from './moduller/Kamusal.js';
import type { Izin } from '@nero/shared';

/* Ağır motorlar yalnızca o ekran açıldığında indirilir. */
const LogAnalizGetir = () => import('./moduller/LogAnaliz.js');
const PostStudioGetir = () => import('./moduller/PostStudio.js');
const LogAnaliz = lazy(() => LogAnalizGetir().then((m) => ({ default: m.LogAnaliz })));
const PostStudio = lazy(() => PostStudioGetir().then((m) => ({ default: m.PostStudio })));

/**
 * AĞIR EKRANLAR BOŞ ZAMANDA ÖNDEN İNDİRİLİR.
 *
 * Post Studio ve Log Analiz ayrı parçalardır: ilk kez açıldıklarında
 * indirme + ayrıştırma bekleniyor ve ekranda "yükleniyor" görünüyordu.
 * ÖLÇÜLDÜ (gerçek Chromium): Post Studio'ya ilk geçiş 160 ms, ikinci
 * geçiş 61 ms — aradaki fark tam olarak bu bekleme.
 *
 * Parçalar artık uygulama açıldıktan sonra, tarayıcı BOŞTAYKEN sessizce
 * indirilir. Açılış hızına dokunmaz (`requestIdleCallback` ilk boyamayı
 * bekler), ilk tıklamada ekran hazır gelir.
 *
 * HEIC çözücüsü (1,9 MB wasm) buraya BİLEREK konmadı: yalnızca gerçekten
 * bir HEIC dosyası açılırken gerekir ve boşuna indirmek, kazandığından
 * çok daha pahalıya mal olurdu.
 */
function agirEkranlariOndenIndir(): () => void {
  let iptal = false;
  const indir = () => {
    if (iptal) return;
    void PostStudioGetir().catch(() => { /* çevrimdışı: ekran açılınca yine denenir */ });
    void LogAnalizGetir().catch(() => { /* aynı */ });
  };
  const bosZaman = (globalThis as { requestIdleCallback?: (f: () => void, o?: { timeout: number }) => number })
    .requestIdleCallback;
  if (bosZaman) {
    const kimlik = bosZaman(indir, { timeout: 4000 });
    return () => {
      iptal = true;
      (globalThis as { cancelIdleCallback?: (k: number) => void }).cancelIdleCallback?.(kimlik);
    };
  }
  /* Safari'de `requestIdleCallback` yok; kısa bir gecikme aynı işi görür. */
  const z = window.setTimeout(indir, 1500);
  return () => { iptal = true; window.clearTimeout(z); };
}

/** Yetkisiz kullanıcı bölüme hiç giremez; sunucu zaten reddeder, burada da gösterilmez. */
function Korumali({ izin, children }: { izin: Izin; children: React.ReactNode }) {
  const { yetkili } = useOturum();
  if (!yetkili(izin)) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/**
 * GİRİŞSİZ AÇILAN SAYFALAR.
 *
 * Google, Drive iznini onaylamadan önce uygulamanın tanıtım, gizlilik ve
 * koşullar sayfalarını herkese açık bir adreste görmek ister; inceleyen
 * kişinin Nero Pro hesabı yoktur. Bu üç rota bu yüzden giriş kontrolünün
 * ÖNÜNDE döndürülür.
 *
 * Kapsam bilerek dardır: yalnızca `KAMUSAL_YOLLAR` listesindeki üç adres.
 * Başka hiçbir ekran girişsiz açılmaz — oturum mimarisine dokunulmadı.
 */
function KamusalSayfalar() {
  return (
    <Routes>
      <Route path="/oauth/nero-pro-backup" element={<OauthTanitim />} />
      <Route path="/oauth/privacy" element={<Gizlilik />} />
      <Route path="/oauth/terms" element={<Kosullar />} />
    </Routes>
  );
}

export function Uygulama() {
  const { kullanici, hazir } = useOturum();
  const { pathname } = useLocation();
  const kamusal = kamusalMi(pathname);

  /* Giriş yapılmadan indirmenin anlamı yok: giriş ekranındaki kullanıcı
     o parçaları hiç açmayabilir. Kamusal sayfalarda da gerekmez.
     KANCA SIRASI: bu `useEffect` her render'da AYNI sırada çalışmalı,
     bu yüzden erken `return`'lerin ÜSTÜNDEDİR. */
  const girisYapildi = !!kullanici && !kamusal;
  useEffect(() => {
    if (!girisYapildi) return undefined;
    return agirEkranlariOndenIndir();
  }, [girisYapildi]);

  /* Giriş durumu HENÜZ BİLİNMEDEN de açılır: Google'ın inceleme robotu
     oturum yoklamasını beklemek zorunda kalmasın. */
  if (kamusal) return <KamusalSayfalar />;

  if (!hazir) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100%' }}>
        <div className="iskelet" style={{ width: 180, height: 8 }} />
      </div>
    );
  }

  if (!kullanici) return <Giris />;

  return (
    <Kabuk>
      <Suspense fallback={<div className="sayfa"><Yukleniyor /></div>}>
        <Routes>
          <Route path="/" element={<AnaSayfa />} />
          <Route path="/is-emirleri" element={<Korumali izin="is_emri.goruntule"><IsEmirleri /></Korumali>} />
          <Route path="/is-emirleri/:id" element={<Korumali izin="is_emri.goruntule"><IsEmriDetayi /></Korumali>} />
          <Route path="/musteriler" element={<Korumali izin="musteri.goruntule"><Musteriler /></Korumali>} />
          <Route path="/musteriler/:id" element={<Korumali izin="musteri.goruntule"><MusteriDetayi /></Korumali>} />
          <Route path="/katalog" element={<Korumali izin="fiyat.goruntule"><Katalog /></Korumali>} />
          {/* Kasa rotası MENÜYLE AYNI İZNE bakar (`kasa.kayit`): personel
              girdi/çıktı yazabildiği için ekranı açabilmeli. Menüde
              görünüp tıklayınca ana sayfaya atmak, düzeltmeye çalıştığımız
              hatanın ta kendisi olurdu. Toplam/bakiye kutuları ekranın
              içinde ayrıca gizlenir (Kasa.tsx · finansGor). */}
          <Route path="/kasa" element={<Korumali izin="kasa.kayit"><Kasa /></Korumali>} />
          <Route path="/proforma" element={<Korumali izin="proforma.goruntule"><Proforma /></Korumali>} />
          <Route path="/proforma/:id" element={<Korumali izin="proforma.goruntule"><ProformaDetayi /></Korumali>} />
          <Route path="/stok" element={<Korumali izin="stok.goruntule"><Stok /></Korumali>} />
          <Route path="/log-analiz" element={<Korumali izin="log.kullan"><LogAnaliz /></Korumali>} />
          <Route path="/post-studio" element={<Korumali izin="post.kullan"><PostStudio /></Korumali>} />
          <Route path="/yonetici/*" element={<Korumali izin="rapor.goruntule"><Yonetici /></Korumali>} />
          <Route path="/ayarlar" element={<Ayarlar />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Kabuk>
  );
}
